import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CaretDown,
  CaretRight,
  DotsThreeOutline,
  Folder as FolderIcon,
  FolderLock,
  FolderUser,
} from '@phosphor-icons/react';
import React from 'react';
import { Folder } from '../../../../util/Types/Folder';
import { SidebarAddButton } from './SidebarAddButton';

const HeaderRoot = styled(Box)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
    },
  };
});

interface FolderItemHeaderProps {
  folder: Folder;
  isOpen: boolean;
  isReserved: boolean;
  isHovered: boolean;
  showAddAgent: boolean;
  showMenuButton: boolean;
  menuOpen: boolean;
  headerRef: (node: HTMLDivElement | null) => void;
  onToggle: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onAddAgent?: (folder: Folder) => void;
  onMenuOpen: (event: React.MouseEvent) => void;
}

export const FolderItemHeader: React.FC<FolderItemHeaderProps> = ({
  folder,
  isOpen,
  isReserved,
  isHovered,
  showAddAgent,
  showMenuButton,
  menuOpen,
  headerRef,
  onToggle,
  onMouseEnter,
  onMouseLeave,
  onAddAgent,
  onMenuOpen,
}) => {
  const theme = useTheme();
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];
  const showActions = isHovered || menuOpen;

  return (
    <HeaderRoot
      ref={headerRef}
      onClick={onToggle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1,
          minWidth: 0,
        }}
      >
        {folder.name?.toLowerCase() === 'shared' ? (
          <FolderUser size={16} color={textColor} />
        ) : isReserved ? (
          <FolderLock size={16} color={textColor} />
        ) : (
          <FolderIcon size={16} color={textColor} />
        )}
        <Typography
          sx={{
            color: textColor,
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
          }}
        >
          {folder.name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          color: textColor,
          flexShrink: 0,
        }}
      >
        {showActions && showAddAgent && (
          <SidebarAddButton onClick={() => onAddAgent?.(folder)} />
        )}
        {showActions && showMenuButton && (
          <Box
            component='button'
            onClick={onMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              color: isDarkMode ? palette.gray[400] : palette.gray[500],
              '&:hover': {
                backgroundColor: isDarkMode
                  ? palette.gray[800]
                  : palette.gray[200],
              },
            }}
          >
            <DotsThreeOutline
              size={16}
              weight='fill'
              color={isDarkMode ? palette.gray[400] : palette.gray[500]}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', color: textColor }}>
        {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
      </Box>
    </HeaderRoot>
  );
};
